/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * "Tổng quan" (v2.1): three figures (expiry, next year's renewal price, dossier), the registration
 * record (ours and the registry's), the registrant and dossier, DNS and security at a glance, the
 * orders, invoices and open work of this domain, and the latest events. What needs attention is
 * above the tabs, on every tab.
 */

import { ArrowRight, CalendarClock, FolderOpen, Tag } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api, type DomainDetail } from '../../../api';
import { Badge } from '../../../components/Badge';
import { DataTable, Section } from '../../../components/blocks';
import { CopyText, EmptyState } from '../../../components/States';
import { daysUntil, formatDate, formatDateTime, formatMoney } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { useDomainPage } from '../context';
import { DomainTimelinePanel } from '../DomainPanels';
import { RegistryRef, SubjectRef, useVocab } from '../refs';
import { DOSSIER, LIFECYCLE, StatusBadge } from '../status';
import { UpstreamPanel } from '../UpstreamPanel';
import { domainTabUrl } from './moved';
import { Facts } from './SubjectContactsTab';
import type { DomainTabProps } from './shared';
import { useWorkSummary } from '../../work/shared';

export function Kpi({ icon: Icon, label, value, hint, tone }: { icon: typeof Tag; label: string; value: ReactNode; hint?: ReactNode; tone?: 'warn' | 'crit' | 'ok' }) {
  return (
    <article className="flex min-w-0 flex-col gap-2 rounded-xl border bg-card p-4">
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="size-3.5" aria-hidden="true" /> {label}
      </span>
      <strong className={cn('tabular text-[22px] leading-tight font-semibold tracking-tight break-words', tone === 'crit' && 'text-critical', tone === 'warn' && 'text-warning', tone === 'ok' && 'text-success')}>{value}</strong>
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </article>
  );
}

/** Expiry, the renewal price of one year (default price book), the dossier's file set. */
function Figures({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const d = detail.domain;
  const price = useApi((signal) => api.getTldPriceBreakdown(d.tld_id, { years: 1 }, { signal }), [d.tld_id]);
  const renew = price.status === 'success' ? price.data.operations.find((o) => o.operation === 'RENEW') : undefined;
  const expiry = d.registry_expires_at ?? d.customer_expires_at;
  const left = expiry ? daysUntil(expiry) : null;
  const files = detail.dossier?.vnnic_files;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-overview-figures>
      <Kpi
        icon={CalendarClock}
        label={t('overview.expiry')}
        value={expiry ? formatDate(expiry) : '—'}
        tone={left !== null && left < 0 ? 'crit' : left !== null && left <= 30 ? 'warn' : undefined}
        hint={left === null ? null : left < 0 ? t('overview.expired', { count: -left }) : t('overview.daysLeft', { count: left })}
      />
      <Kpi
        icon={Tag}
        label={t('overview.renewPrice')}
        value={renew && !renew.error ? formatMoney({ amount: renew.total, currency: renew.currency }) : '—'}
        hint={renew?.error || (price.status === 'success' && !renew) ? t('overview.noPrice') : t('overview.renewPriceHint')}
      />
      <Kpi
        icon={FolderOpen}
        label={t('overview.dossier')}
        value={files ? `${files.required.length - files.missing.length}/${files.required.length}` : <StatusBadge map={DOSSIER} value={detail.dossier?.status ?? d.dossier_state} />}
        hint={files ? (files.complete ? t('overview.filesComplete') : t('overview.filesMissing', { count: files.missing.length })) : detail.dossier ? t('overview.versionN', { n: detail.dossier.dossier_version }) : null}
      />
    </div>
  );
}

function RegistrationPanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const vocab = useVocab();
  const { registryState } = useDomainPage();
  const d = detail.domain;
  const snap = registryState?.snapshot ?? null;
  return (
    <Section title={t('overview.registration')} description={t('overview.registrationHint')}>
      <Facts
        rows={[
          [t('overview.ascii'), <span key="a" className="font-mono">{d.ascii_name}</span>],
          [t('overview.unicode'), d.unicode_name],
          [t('overview.domainId'), <CopyText key="id" value={d.id} />],
          [t('overview.roid'), snap?.roid ? <span key="r" className="font-mono">{snap.roid}</span> : t('overview.notRead')],
          [t('overview.createdAt'), formatDateTime(d.customer_created_at ?? d.created_at)],
          [t('overview.registryCreated'), d.registry_created_at ? formatDateTime(d.registry_created_at) : null],
          [t('overview.registryExpiry'), d.registry_expires_at ? formatDate(d.registry_expires_at) : null],
          [t('overview.paidThrough'), d.customer_expires_at ? formatDate(d.customer_expires_at) : null],
          [t('overview.tld'), `.${d.tld} · ${vocab('tldClass', d.tld_class)}`],
          [t('overview.registry'), <RegistryRef key="reg" code={d.registry} name={d.registry_name} />],
          [t('overview.sponsor'), snap?.sponsor ?? null],
          [t('overview.lifecycle'), <StatusBadge key="l" map={LIFECYCLE} value={d.lifecycle_state} />],
          [t('overview.snapshot'), snap ? formatDateTime(snap.captured_at) : t('overview.notRead')],
          [t('overview.record'), t('overview.recordValue', { version: d.version, at: formatDateTime(d.updated_at) })],
        ]}
      />
    </Section>
  );
}

function SubjectDossierPanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const vocab = useVocab();
  const d = detail.domain;
  const x = detail.dossier;
  return (
    <Section title={t('overview.subjectDossier')}>
      <div className="mb-4">
        <SubjectRef id={d.current_subject_id} name={d.subject_display_name} type={d.subject_type} />
      </div>
      <Facts
        rows={[
          [t('overview.subjectVersion'), x?.subject_version_number ? t('overview.versionN', { n: x.subject_version_number }) : null],
          [
            t('overview.dossierLine'),
            x ? (
              <span key="x" className="flex flex-wrap items-center gap-2">
                {t('overview.versionN', { n: x.dossier_version })} <StatusBadge map={DOSSIER} value={x.status} />
              </span>
            ) : (
              <StatusBadge key="x" map={DOSSIER} value={d.dossier_state} />
            ),
          ],
          [
            t('overview.verification'),
            x?.verification_method ? (
              <span key="v" className="flex flex-wrap items-center gap-2">
                {vocab('method', x.verification_method)}
                {x.verification_status ? <Badge tone={x.verification_status === 'VERIFIED' ? 'ok' : 'warn'}>{vocab('check', x.verification_status)}</Badge> : null}
              </span>
            ) : null,
          ],
          [t('overview.completed'), x?.completed_at ? formatDateTime(x.completed_at) : null],
        ]}
      />
      <div className="mt-4 flex flex-wrap gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={domainTabUrl(d.ascii_name, 'subject')}>
            {t('overview.openSubject')} <ArrowRight />
          </Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to={domainTabUrl(d.ascii_name, 'dossier')}>
            {t('overview.openDossier')} <ArrowRight />
          </Link>
        </Button>
      </div>
    </Section>
  );
}

function DnsSecurityPanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const { delegation } = useDomainPage();
  const d = detail.domain;
  const locked = detail.registry_statuses.some((s) => s.status === 'clientTransferProhibited');
  return (
    <Section
      title={t('overview.dnsSecurity')}
      actions={
        <Button asChild variant="ghost" size="sm">
          <Link to={domainTabUrl(d.ascii_name, 'dns')}>
            {t('overview.manage')} <ArrowRight />
          </Link>
        </Button>
      }
    >
      <Facts
        rows={[
          [
            t('overview.nameservers'),
            delegation ? (
              delegation.nameservers.length ? (
                <span key="n" className="grid font-mono text-[13px]">
                  {delegation.nameservers.map((n) => (
                    <span key={n.host}>{n.host}</span>
                  ))}
                </span>
              ) : (
                t('overview.none')
              )
            ) : (
              '…'
            ),
          ],
          [
            t('overview.dnssec'),
            delegation ? (
              <Badge key="s" tone={delegation.ds_records.length ? 'ok' : 'neutral'} icon={false}>
                {delegation.ds_records.length ? t('overview.dnssecOn', { count: delegation.ds_records.length }) : t('overview.dnssecOff')}
              </Badge>
            ) : (
              '…'
            ),
          ],
          [
            t('overview.transferLock'),
            <Badge key="l" tone={locked ? 'ok' : 'neutral'} icon={false}>
              {locked ? t('overview.on') : t('overview.off')}
            </Badge>,
          ],
          [
            t('overview.autoRenew'),
            <Badge key="a" tone={d.auto_renew ? 'ok' : 'neutral'} icon={false}>
              {d.auto_renew ? t('overview.on') : t('overview.off')}
            </Badge>,
          ],
        ]}
      />
    </Section>
  );
}

interface RelatedRow {
  key: string;
  kind: string;
  ref: ReactNode;
  state: ReactNode;
  to: string;
}

/** Orders, invoices and open work of this domain, each opening its page. */
function RelatedPanel({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domainPage');
  const vocab = useVocab();
  const d = detail.domain;
  const commerce = useApi((signal) => api.getDomainCommerce(d.id, { signal }), [d.id]);
  const work = useApi((signal) => api.listWorkItems({ status: 'OPEN', domain_id: d.id, limit: 20 }, { signal }), [d.id]);
  const summarize = useWorkSummary();
  const { t: tw } = useTranslation('work');
  const rows: RelatedRow[] = [];
  if (commerce.status === 'success') {
    for (const o of commerce.data.items.slice(0, 5)) {
      rows.push({
        key: `o-${o.id}`,
        kind: t('overview.order', { operation: vocab('operation', o.operation), count: o.years }),
        ref: <span className="font-mono text-xs">{o.order_id.slice(0, 8)}</span>,
        state: <Badge tone={o.status === 'COMPLETED' ? 'ok' : o.status === 'FAILED' ? 'serious' : 'pending'}>{vocab('orderStatus', o.status)}</Badge>,
        to: `/orders/${encodeURIComponent(o.order_id)}`,
      });
    }
    for (const i of commerce.data.invoices.slice(0, 5)) {
      rows.push({
        key: `i-${i.id}`,
        kind: i.document_type === 'CREDIT_NOTE' ? t('overview.creditNote') : t('overview.invoice'),
        ref: <span className="font-mono text-xs">{`${i.series}-${i.number}`}</span>,
        state: formatMoney({ amount: i.total, currency: i.currency }),
        to: `/invoices/${encodeURIComponent(i.id)}`,
      });
    }
  }
  if (work.status === 'success') {
    for (const w of work.data.items) {
      rows.push({
        key: `w-${w.id}`,
        kind: summarize(w).text ?? tw(`kind.${w.kind}`),
        ref: <span className="font-mono text-xs">{w.id.slice(0, 8)}</span>,
        state: <Badge tone={w.severity === 'CRITICAL' || w.severity === 'HIGH' ? 'serious' : 'warn'}>{t('overview.workOpen')}</Badge>,
        to: `/compliance/work/${encodeURIComponent(w.id)}`,
      });
    }
  }
  return (
    <Section flush title={t('overview.related')} description={t('overview.relatedHint', { domain: d.unicode_name })}>
      <DataTable<RelatedRow>
        columns={[
          { key: 'kind', header: t('overview.relatedKind'), className: 'min-w-44 whitespace-normal', cell: (r) => r.kind },
          { key: 'ref', header: t('overview.relatedRef'), cell: (r) => r.ref },
          { key: 'state', header: t('overview.relatedState'), cell: (r) => r.state },
          {
            key: 'open',
            header: '',
            cell: (r) => (
              <Link to={r.to} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                {t('overview.openRow')} <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            ),
          },
        ]}
        rows={rows}
        rowKey={(r) => r.key}
        empty={<EmptyState title={commerce.status === 'loading' || work.status === 'loading' ? t('overview.loading') : t('overview.noRelated')} />}
      />
    </Section>
  );
}

export function OverviewTab({ detail }: DomainTabProps) {
  return (
    <>
      <Figures detail={detail} />
      <RegistrationPanel detail={detail} />
      <UpstreamPanel domainId={detail.domain.id} />
      <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
        <SubjectDossierPanel detail={detail} />
        <DnsSecurityPanel detail={detail} />
      </div>
      <RelatedPanel detail={detail} />
      <DomainTimelinePanel detail={detail} />
    </>
  );
}
